import { type PropsWithChildren, useCallback, useState } from 'react'
import { Badge, Box, Drawer, IconButton, Stack } from '@mui/material'
import { CloseRounded as CloseRoundedIcon, TuneRounded as TuneRoundedIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

type Props = PropsWithChildren<{
  activeCount?: number
  inDialog?: boolean
}>

export default function AccountFilterDrawer(props: Props) {
  const [t] = useTranslation('account-v2')
  const [opened, setOpened] = useState(false)

  const onOpen = useCallback(() => {
    setOpened(true)
  }, [])

  const onClose = useCallback(() => {
    setOpened(false)
  }, [])

  return <>
    <IconButton
      aria-label={t('Open filters')}
      onClick={onOpen}
      sx={{
        flexShrink: 0,
      }}
    >
      <Badge
        color="primary"
        badgeContent={props.activeCount}
        invisible={(props.activeCount ?? 0) === 0}
      >
        <TuneRoundedIcon />
      </Badge>
    </IconButton>

    <Drawer
      anchor="right"
      open={opened}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: props.inDialog ? theme => theme.zIndex.modal + 1 : 1290,
        },
      }}
      PaperProps={{
        sx: {
          top: props.inDialog ? 0 : {
            xs: '52px',
            md: '64px',
          },
          height: props.inDialog ? '100dvh' : {
            xs: 'calc(100dvh - 52px)',
            md: 'calc(100dvh - 64px)',
          },
          width: {
            xs: '100%',
            sm: 380,
          },
          maxWidth: '100vw',
        },
      }}
    >
      <Box
        component="div"
        sx={{
          position: 'relative',
          height: '100%',
        }}
      >
        <IconButton
          aria-label={t('Close filters')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            zIndex: 1,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>

        <Box
          component="div"
          sx={{
            height: '100%',
            flexGrow: 1,
            overflowY: 'auto',
            px: 3,
            pt: 2,
            pb: 3,
          }}
        >
          <Stack spacing={3}>{props.children}</Stack>
        </Box>
      </Box>
    </Drawer>
  </>
}
